// Public /places page — a map of every place-y group with a location.
// Hover a pin for details (name/category/address/phone/website), click
// to open the group page. Popup has a "Navigate here" link that hands
// coordinates to Google Maps for turn-by-turn.
//
// Post-0037: places ARE groups. Unclaimed groups (from the future OSM
// crawler) render with a grey pin + "Unclaimed" badge + a "Claim it"
// affordance on the popup.
//
// Default view zooms to Vredehoek — the launch community. Change here
// when we roll to a second community.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as maplibregl from 'maplibre-gl';
import { type LngLatLike, type Map as MLMap, Popup, NavigationControl, Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatMastheadDate } from '../lib/format';

const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
// Launch community. Vredehoek + immediate surrounds. Zoom 14 gets the
// suburb + City Bowl edges in a comfortable single view.
const VREDEHOEK: LngLatLike = [18.4152, -33.9376];
const DEFAULT_ZOOM = 14;

interface Place {
    id: string;
    slug: string;
    name: string;
    category: string;
    address?: string | null;
    lat?: number | null;
    lng?: number | null;
    website?: string | null;
    phone?: string | null;
    claim_status: 'unclaimed' | 'pending' | 'claimed' | 'disputed';
}

export function Places() {
    const api = useApi();
    const { authed, login } = useAuth();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<MLMap | null>(null);
    const markersRef = useRef<Marker[]>([]);
    const [places, setPlaces] = useState<Place[]>([]);
    const [category, setCategory] = useState('');
    const [ready, setReady] = useState(false);

    const load = useCallback(async () => {
        if (!authed) return;
        const q = category ? '?category=' + encodeURIComponent(category) : '';
        try {
            const r = await api.get<{ places: Place[] }>('/api/places' + q);
            setPlaces(r.places ?? []);
        } catch (e) { console.error('places load', e); }
    }, [api, authed, category]);
    useEffect(() => { load(); }, [load]);

    // Boot the map once.
    useEffect(() => {
        if (!containerRef.current || mapRef.current || !authed) return;
        const m = new maplibregl.Map({
            container: containerRef.current,
            style: mapStyle(),
            center: VREDEHOEK,
            zoom: DEFAULT_ZOOM,
        });
        m.addControl(new NavigationControl(), 'top-right');
        m.on('load', () => setReady(true));
        setTimeout(() => setReady(true), 1500);   // belt & braces
        mapRef.current = m;
        return () => { mapRef.current?.remove(); mapRef.current = null; };
    }, [authed]);

    // (Re)render markers whenever places or ready flips.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !ready) return;
        while (markersRef.current.length) markersRef.current.pop()!.remove();
        const withCoords = places.filter(p => p.lat != null && p.lng != null);
        for (const p of withCoords) {
            const el = document.createElement('div');
            const isUnclaimed = p.claim_status === 'unclaimed';
            el.style.cssText = [
                'width: 20px', 'height: 20px', 'border-radius: 50%',
                `background: ${isUnclaimed ? '#a1a1a1' : '#2b5e3f'}`,
                'border: 2px solid #fff',
                'box-shadow: 0 2px 6px rgba(0,0,0,0.35)', 'cursor: pointer',
            ].join(';');
            el.title = p.name;

            const popup = new Popup({ closeButton: false, offset: 14 })
                .setHTML(placePopupHTML(p));
            el.addEventListener('mouseenter', () => popup.setLngLat([p.lng!, p.lat!]).addTo(map));
            el.addEventListener('mouseleave', () => popup.remove());
            el.addEventListener('click', () => {
                // Public business profile — dashboard link lives on the profile
                // page itself for owners. Passers-by land on the profile, not
                // the owner-only stats view.
                window.location.href = '/groups/' + p.slug;
            });
            const m = new Marker({ element: el }).setLngLat([p.lng!, p.lat!]).addTo(map);
            markersRef.current.push(m);
        }
    }, [places, ready]);

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to see local businesses.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );

    const withCoords   = places.filter(p => p.lat != null).length;
    const unclaimed    = places.filter(p => p.claim_status === 'unclaimed').length;
    const withoutMap   = places.filter(p => p.lat == null);

    return (
        <main>
            <div className="paper-wrap">
                <div className="masthead">
                    <div className="rule-top" />
                    <h1>Places</h1>
                    <div className="issue">{formatMastheadDate()}</div>
                    <div className="rule-bottom" />
                </div>

                <div className="classifieds-toolbar" style={{ marginBottom: 12 }}>
                    <select value={category} onChange={(e) => setCategory(e.target.value)}>
                        <option value="">All categories</option>
                        {['cafe','restaurant','retail','salon','service','grocery','community','other'].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: '#666' }}>
                        {withCoords} on map · {unclaimed} unclaimed
                    </span>
                </div>

                <div ref={containerRef}
                     className="newsprint-map"
                     style={{ width: '100%', height: 'calc(100vh - 260px)', minHeight: 460, border: '1px solid var(--ink)' }} />

                <p style={{ fontSize: 11, color: '#666', marginTop: 8, fontFamily: "'Newsreader',serif", fontStyle: 'italic' }}>
                    Green pins are claimed places. Grey pins are unclaimed — the business owner can claim their listing from the place page.
                </p>

                {withoutMap.length > 0 && (
                    <section style={{ marginTop: 20 }}>
                        <h2 style={{ fontFamily: '-apple-system,sans-serif', fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, borderBottom: '1px solid var(--ink)', paddingBottom: 4 }}>
                            Places without a map pin ({withoutMap.length})
                        </h2>
                        {withoutMap.map(p => (
                            <div key={p.id} style={{ padding: '6px 0', borderBottom: '1px dotted #cfc9b7' }}>
                                <Link to={'/groups/' + p.slug}>{p.name}</Link>
                                <span style={{ marginLeft: 8, fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>{p.category}</span>
                                {p.address && <div style={{ fontSize: 12, color: '#555' }}>{p.address}</div>}
                            </div>
                        ))}
                    </section>
                )}
            </div>
        </main>
    );
}

function placePopupHTML(p: Place): string {
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const navHref = `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`;
    const claimBadge = p.claim_status === 'unclaimed'
        ? `<span style="display:inline-block;padding:1px 6px;background:#faf8f2;border:1px solid #999;color:#666;font-size:9px;text-transform:uppercase;letter-spacing:1.5px;font-family:-apple-system,sans-serif;margin-left:6px;">Unclaimed</span>`
        : '';
    const websiteLink = p.website
        ? `<div style="margin-top:4px;font-size:11px;"><a href="${esc(p.website)}" target="_blank" rel="noopener noreferrer">${esc(p.website)}</a></div>`
        : '';
    const phoneLine = p.phone
        ? `<div style="margin-top:2px;font-size:12px;color:#333;">☎ ${esc(p.phone)}</div>`
        : '';
    const claimCall = p.claim_status === 'unclaimed'
        ? `<div style="margin-top:6px;font-size:11px;font-style:italic;color:#8b1919;">Own this business? <a href="/groups/${esc(p.slug)}">Claim it →</a></div>`
        : '';
    return `
        <div style="font-family:'Newsreader',serif;min-width:200px;">
            <div style="font-weight:700;font-size:15px;">${esc(p.name)}${claimBadge}</div>
            <div style="font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#666;">${esc(p.category)}</div>
            ${p.address ? `<div style="font-size:12px;margin-top:4px;color:#333;">${esc(p.address)}</div>` : ''}
            ${phoneLine}
            ${websiteLink}
            <div style="margin-top:8px;display:flex;gap:8px;align-items:center;font-size:11px;">
                <a href="${navHref}" target="_blank" rel="noopener noreferrer"
                   style="padding:2px 8px;background:#2b5e3f;color:#fff;text-decoration:none;text-transform:uppercase;letter-spacing:1px;font-family:-apple-system,sans-serif;">
                    🧭 Navigate
                </a>
                <span style="color:#888;">Click pin for details →</span>
            </div>
            ${claimCall}
        </div>`;
}

function mapStyle(): maplibregl.StyleSpecification {
    return {
        version: 8,
        sources: {
            osm: { type: 'raster', tiles: [OSM_TILE_URL], tileSize: 256, attribution: '© OpenStreetMap contributors' },
        },
        layers: [
            { id: 'bg',  type: 'background', paint: { 'background-color': '#fdfcf6' } },
            { id: 'osm', type: 'raster', source: 'osm', paint: { 'raster-opacity': 0.85 } },
        ],
    } as maplibregl.StyleSpecification;
}
